import { styled } from "goober";
import { type ReactNode, useRef } from "react";
import { Icon } from "../../components/Icon";
import { Text } from "../../components/Text";
import { CircleMesh } from "../CircleMesh";
import { PressButton } from "../PressButton";

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  align-items: center;
  text-align: center;
  justify-content: center;
  gap: 8px;
  position: relative;
`;

const Inner = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: center;
`;

const Footer = styled("div")`
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 32px;
  display: flex;
  justify-content: center;
`;

const LegalLink = styled("a")`
  color: inherit;
  text-decoration-color: var(--border-alpha);
  text-underline-offset: 2px;

  &:hover {
    color: var(--text-primary);
  }
`;

export const SignInButton = styled(PressButton)`
  padding: 12px 20px;
  border: none;
  border-radius: 9999px;
  color: var(--text-primary);
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-width: 240px;
  font-family: inherit;

  &[data-muted="true"] > * {
    opacity: 0.5;
  }

  &[data-muted="true"]:hover > * {
    opacity: 1;
  }

  &:hover {
    background: var(--surface-hover);
  }

  &:active {
    opacity: 0.8;
  }
`;

export function SignInLayout({
  children,
  title = "Welcome to 3B",
}: {
  children: ReactNode;
  title?: string;
}) {
  const randomSeed = useRef(Math.random().toString());

  return (
    <Container>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: -1,
        }}
      >
        <CircleMesh
          seed={randomSeed.current}
          targetCircleSize={115}
          mutingIntensity={1}
        />
      </div>
      <Inner>
        <Icon name="tines-mark" size={36} />
        <Text variant="display-md">{title}</Text>
        {children}
      </Inner>
      <Footer>
        <Text variant="text-small" color="var(--text-secondary)">
          By accessing or using the services, you agree to the terms set forth
          at{" "}
          <LegalLink
            href="https://tines.com/legal"
            target="_blank"
            rel="noopener noreferrer"
          >
            tines.com/legal
          </LegalLink>
          .
        </Text>
      </Footer>
    </Container>
  );
}
